import { useStore } from '../store/StoreContext.jsx';
import Field from './Field.jsx';
import DataTable from './DataTable.jsx';
import ConditionalBlock from './ConditionalBlock.jsx';
import { SectionShell } from './FormSection.jsx';
import { isHouseholdOwned, HOUSEHOLD_LOCK_HINT } from '../schema/householdOwned.js';

export default function FamilySection({ section, num }) {
  const { data, update, stripKeysMatching, householdMode } = useStore();
  const sectionData = data[section.id] || {};
  const flags = data._flags || {};

  const renderField = f => {
    let field = f;
    // Launched from a household rather than a lead: who these people are
    // belongs to the household record, and the payload withholds it, so the
    // form shows it rather than inviting an edit that would not save.
    if (householdMode && isHouseholdOwned(section.id, f.key)) {
      return (
        <Field
          key={f.key}
          field={{ ...f, locked: true, lockHint: HOUSEHOLD_LOCK_HINT }}
          value={sectionData[f.key]}
          onChange={() => {}}
        />
      );
    }
    // Age normally auto-fills from Date of Birth and locks (type: computed).
    // When the matching DOB is blank, let the advisor type the age directly —
    // they may know it without the birth date. useAutoCalcFields won't
    // overwrite a manual age while the DOB is empty, and reclaims it the moment
    // a DOB is entered.
    if (f.key === 'client1Age' || f.key === 'client2Age') {
      const dobKey = f.key === 'client1Age' ? 'client1DOB' : 'client2DOB';
      if (String(sectionData[dobKey] || '').trim() === '') {
        field = { ...f, type: 'number', min: 0, max: 120 };
      }
    }
    return (
      <Field
        key={f.key}
        field={field}
        value={sectionData[f.key]}
        onChange={val => update(`${section.id}.${f.key}`, val)}
      />
    );
  };

  const spouseBlock = section.conditionalBlocks?.find(b => b.id === 'spouse-block');
  const childrenBlock = section.afterConditional?.find(b => b.id === 'children-block');

  const handleSpouseOff = () => {
    stripKeysMatching(k => typeof k === 'string' && k.startsWith('client2'));
    update('family.yearsMarried', undefined);
  };

  return (
    <SectionShell section={section} num={num}>
      {/* Client fields */}
      <div className="subhead">Client</div>
      <div className="grid">{(section.fields || []).map(renderField)}</div>

      {/* Spouse / Partner conditional block */}
      {spouseBlock && (
        <ConditionalBlock
          block={spouseBlock}
          sectionId={section.id}
          onToggleOff={handleSpouseOff}
          // On a household launch the spouse belongs to the household record,
          // like the names. Removing them here cleared the spouse's details on
          // screen while the record kept them, and adding one opened boxes
          // that never save (Aditi, QA 2026-10-15), so the spouse is added or
          // removed on the household in Salesforce instead.
          lockHint={householdMode ? HOUSEHOLD_LOCK_HINT : undefined}
          headerSlot={({ flag, button }) => (
            <>
              <div
                className="subhead"
                style={{ display: 'flex', alignItems: 'center', gap: 10 }}
              >
                {flag ? 'Spouse / Partner' : ''}
                <span style={{ marginLeft: 'auto' }}>{button}</span>
              </div>
              {flag && (
                <div className="grid">{spouseBlock.fields.map(renderField)}</div>
              )}
            </>
          )}
        >
          {/* content rendered via headerSlot */}
        </ConditionalBlock>
      )}

      {/* Children / grandchildren block */}
      {childrenBlock && (
        <ConditionalBlock block={childrenBlock} sectionId={section.id}>
          <div className="children-tables">
            {(childrenBlock.tables || []).map((t, i) => {
              // Pair each table with its matching count (children↔numChildren,
              // grandchildren↔numGrandchildren) so the total sits under its own
              // table instead of in a shared row below both.
              const summary = childrenBlock.summaryFields?.[i];
              // Once the table has populated rows, the count is derived (kept
              // in sync by useAutoCalcFields) — render it read-only so it
              // doesn't look editable while silently snapping back. It stays
              // an editable fallback only while the table is empty.
              const rows = sectionData[t.key];
              // The grandchildren count always stays editable: it's the number
              // advisors ask for, and names are optional (see useAutoCalcFields).
              const derived = t.key !== 'grandchildren' && Array.isArray(rows) && rows.some(
                r => String(r?.name || '').trim() !== '' || String(r?.age || '').trim() !== ''
              );
              return (
                <div className="children-tables-col" key={t.key}>
                  <DataTable sectionId={section.id} tableDef={t} />
                  {summary && (
                    <div className="grid children-count">
                      {renderField(derived ? { ...summary, type: 'computed' } : summary)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </ConditionalBlock>
      )}

      {/* Footer fields (additional family info), then Goals directly beneath.
          Goals used to live in the slide-out drawer, where advisors forgot it
          existed: "up here, they're not going to see it... out of sight, out of
          mind" (Jake, 2026-08-05). The value still lives at goals.clientGoals,
          so nothing about the stored shape or the Salesforce mapping changes. */}
      <div className="grid" style={{ marginTop: 12 }}>
        {section.footerFields?.map(renderField)}
        <Field
          field={{ key: 'clientGoals', label: 'Goals', type: 'textarea' }}
          value={data.goals?.clientGoals}
          onChange={val => update('goals.clientGoals', val)}
        />
      </div>
    </SectionShell>
  );
}
